/**
 * Sync Status Badge Component
 * Shows sync status with appropriate icons and colors
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Check, AlertTriangle, Clock, Wifi, WifiOff } from 'lucide-react';

export const SYNC_STATUS = {
  PENDING: 'PENDING',
  SYNCING: 'SYNCING',
  SYNCED: 'SYNCED',
  FAILED: 'FAILED',
};

export function SyncStatusBadge({ status, onRetry, retryCount = 0, compact = false }) {
  const config = {
    [SYNC_STATUS.PENDING]: {
      icon: Clock,
      label: 'Pending Sync',
      variant: 'secondary',
      className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    },
    [SYNC_STATUS.SYNCING]: {
      icon: RefreshCw,
      label: 'Syncing...',
      variant: 'secondary',
      className: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      animate: true,
    },
    [SYNC_STATUS.SYNCED]: {
      icon: Check,
      label: 'Synced',
      variant: 'secondary',
      className: 'bg-green-500/10 text-green-600 border-green-500/20',
    },
    [SYNC_STATUS.FAILED]: {
      icon: AlertTriangle,
      label: retryCount > 0 ? `Failed (${retryCount} retries)` : 'Sync Failed',
      variant: 'destructive',
      className: 'bg-red-500/10 text-red-600 border-red-500/20',
    },
  };

  const { icon: Icon, label, className, animate } = config[status] || config[SYNC_STATUS.PENDING];

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${className}`}>
        <Icon className={`h-3 w-3 ${animate ? 'animate-spin' : ''}`} />
        {status === SYNC_STATUS.FAILED && onRetry && (
          <button onClick={onRetry} className="ml-1 underline">Retry</button>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${className}`}>
      <Icon className={`h-4 w-4 ${animate ? 'animate-spin' : ''}`} />
      <span className="text-sm font-medium">{label}</span>
      {status === SYNC_STATUS.FAILED && onRetry && (
        <Button size="sm" variant="outline" onClick={onRetry} className="ml-auto h-7 text-xs">
          <RefreshCw className="h-3 w-3 mr-1" />
          Retry Sync
        </Button>
      )}
    </div>
  );
}

export function OfflineIndicator({ isOffline }) {
  if (!isOffline) return null;
  
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 text-amber-600 border border-amber-500/20">
      <WifiOff className="h-4 w-4" />
      <span className="text-sm font-medium">Offline - Changes will sync when connected</span>
    </div>
  );
}

export function DraftBadge() {
  return (
    <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
      <Clock className="h-3 w-3 mr-1" />
      Draft
    </Badge>
  );
}

export default SyncStatusBadge;
